import { useCallback, useEffect, useRef, useState } from "react";
import {
  useNoteMediaUpload,
  type UploadedMedia,
} from "#app/lib/hooks/use-note-media";

/** A photo upload in flight — rendered as an optimistic progress tile until it
 * lands (success → `onUploaded`) or is cancelled/fails (tile drops). */
export interface PendingMediaUpload {
  tempId: string;
  key: string | null;
  /** Local preview, null until client-side processing finishes. */
  uri: string | null;
  /** Display-byte PUT progress, 0–100. */
  progress: number;
}

/**
 * Owns the optimistic, cancellable, abort-on-unmount upload state shared by
 * every surface that attaches photos on-pick (the note composer's footer
 * attachments, photos-type custom fields). Each picked file becomes a
 * `pending` tile that reports preview + progress, then either commits via
 * `onUploaded` or drops on cancel/failure.
 *
 * Call sites keep ONLY their commit logic — the composer appends to its
 * attachment cards, a custom field emits the new id list. Everything else
 * (temp ids, abort controllers, the in-flight tile array, unmount cleanup)
 * lives here.
 */
export function usePendingMediaUploads(opts: {
  onUploaded: (result: UploadedMedia, key: string | null) => void | Promise<void>;
}): {
  pending: PendingMediaUpload[];
  /**
   * Upload each file concurrently but COMMIT them in pick order; resolves once
   * all settle, returning the committed photos in that pick order (failed /
   * cancelled uploads omitted). `key` rides every tile and commit of the batch.
   */
  startUploads: (files: File[], key?: string) => Promise<Array<UploadedMedia>>;
  /** Abort one in-flight upload and drop its tile. */
  cancel: (tempId: string) => void;
  /** Abort everything in flight and clear the tiles (composer reset). */
  reset: () => void;
} {
  const { uploadNotePhoto } = useNoteMediaUpload();
  const [pending, setPending] = useState<PendingMediaUpload[]>([]);
  const idRef = useRef(0);
  // Abort controllers keyed by temp id — kept in a ref (not render state) so
  // cancel and unmount can abort the XHR.
  const controllers = useRef<Map<string, AbortController>>(new Map());
  // Latest commit callback in a ref so `startUploads`/`uploadOne` keep a stable
  // identity even as the call site passes a fresh closure each render.
  const onUploadedRef = useRef(opts.onUploaded);
  onUploadedRef.current = opts.onUploaded;

  // Uploads one file and returns a commit() thunk to run later. Upload and
  // commit are split so a batch can upload concurrently (the slow part) yet
  // commit in pick order — see startUploads. commit() is fire-and-forget: it
  // drops the tile and fires onUploaded without awaiting, so the per-photo
  // adds still race to the server harmlessly (NOT serialized — awaiting them
  // in order stretched the batch over seconds and lost photos). It returns the
  // committed result (or null if aborted/failed) so startUploads can collect
  // the batch in pick order.
  const uploadOne = useCallback(
    async (
      file: File,
      key: string | null,
    ): Promise<() => UploadedMedia | null> => {
      const tempId = `pending-${idRef.current++}`;
      const controller = new AbortController();
      controllers.current.set(tempId, controller);
      setPending((prev) => [...prev, { key, progress: 0, tempId, uri: null }]);
      const update = (patch: Partial<PendingMediaUpload>) =>
        setPending((prev) =>
          prev.map((p) => (p.tempId === tempId ? { ...p, ...patch } : p)),
        );
      const result = await uploadNotePhoto(file, {
        onPreview: (uri) => update({ uri }),
        onProgress: (progress) => update({ progress }),
        signal: controller.signal,
      });
      return () => {
        // An upload that ALREADY resolved can't be aborted, so cancel()/reset()
        // can't stop this continuation — they signal intent via the controller.
        // If it was aborted (the user cancelled this tile, reset cleared the
        // composer, or it was cancelled while waiting its turn to commit), drop
        // the result instead of committing to a surface the user just cleared.
        if (controller.signal.aborted) return null;
        controllers.current.delete(tempId);
        setPending((prev) => prev.filter((p) => p.tempId !== tempId));
        if (!result) return null;
        void onUploadedRef.current(result, key);
        return result;
      };
    },
    [uploadNotePhoto],
  );

  const startUploads = useCallback(
    async (files: File[], key?: string) => {
      if (!files.length) return [];
      // Upload all at once (concurrent PUTs), but COMMIT in pick order: a
      // finished upload waits to commit until every earlier-picked one has, so
      // tiles settle in the order chosen rather than the order PUTs happen to
      // finish — otherwise a late pick that uploads fast jumps ahead and the
      // grid reshuffles as each save lands. Commits stay fire-and-forget, so
      // the batch isn't stretched out: as each contiguous prefix becomes ready
      // it commits immediately. Collect the committed photos in that pick order
      // for the caller (the photos field rewrites its value once in this order).
      const uploads = files.map((file) => uploadOne(file, key ?? null));
      const commits: Array<() => UploadedMedia | null> =
        new Array(files.length);
      const ready = new Array<boolean>(files.length).fill(false);
      const committed: Array<UploadedMedia> = [];
      let next = 0;
      await Promise.all(
        uploads.map(async (u, i) => {
          commits[i] = await u;
          ready[i] = true;
          // The commit for a ready slot is always present: `ready[n]` only
          // flips in the same tick that fills `commits[n]`.
          while (next < files.length && ready[next]) {
            const result = commits[next]?.();
            if (result) committed.push(result);
            next += 1;
          }
        }),
      );
      return committed;
    },
    [uploadOne],
  );

  const cancel = useCallback((tempId: string) => {
    controllers.current.get(tempId)?.abort();
    controllers.current.delete(tempId);
    setPending((prev) => prev.filter((p) => p.tempId !== tempId));
  }, []);

  const reset = useCallback(() => {
    for (const controller of controllers.current.values()) controller.abort();
    controllers.current.clear();
    setPending([]);
  }, []);

  // Abort any uploads still in flight if the host unmounts.
  useEffect(() => {
    const map = controllers.current;
    return () => {
      for (const controller of map.values()) controller.abort();
    };
  }, []);

  return { pending, startUploads, cancel, reset };
}
